import { zodResolver } from "@hookform/resolvers/zod";
import {
  createFileRoute,
  Navigate,
  Outlet,
  useMatch,
  useRouterState,
} from "@tanstack/react-router";
import { Suspense, useEffect, useMemo } from "react";
import { FormProvider, useForm, useFormContext } from "react-hook-form";
import { useTranslations } from "use-intl";

import {
  PurchaseStateContextProvider,
  usePurchaseStateContext,
} from "@/contexts/PurchaseStateContext";
import { OverviewCardSkeleton } from "@/features/trips/components/bookingFlow/tripDetails/OverviewCard";
import type { BookingData } from "@/features/trips/types/types";
import { createValidationSchema } from "@/features/trips/utils/validation";

function ValidationTrigger() {
  const router = useRouterState();

  const { trigger } = useFormContext<BookingData>();

  // react-hook-form, by default doesn't validate on mount
  // this causes fields to be considered valid until user interacts with them
  // which leads to issues - we want to have proper info about form validity right away
  // to work around this, we trigger validation on mount and on route change
  useEffect(() => {
    trigger();
  }, [trigger, router.location.pathname]);

  return null;
}

export const Route = createFileRoute("/_authenticated/trips/$tripId")({
  component: RouteComponent,
});

function RouteComponent() {
  return (
    <PurchaseStateContextProvider>
      <RouteComponentInner />
    </PurchaseStateContextProvider>
  );
}

function RouteComponentInner() {
  const t = useTranslations();
  const { tripId } = Route.useParams();
  const isSuccessPage = !!useMatch({
    from: "/_authenticated/trips/$tripId/success",
    shouldThrow: false,
  });
  const isIndexPage = !!useMatch({
    from: "/_authenticated/trips/$tripId/",
    shouldThrow: false,
  });
  const { purchaseState } = usePurchaseStateContext();

  const validationSchema = useMemo(() => createValidationSchema(t), [t]);

  const methods = useForm({
    resolver: zodResolver(validationSchema),
    mode: "all",
    defaultValues: {
      tripDate: "",
      travellers: {
        adultInSingleRoom: 0,
        adultInDoubleRoom: 0,
        childWithBed: 0,
        childWithoutBed: 0,
        infant: 0,
      },
      selectedTravellers: { ADT: [], CHD: [], INF: [] },
      termsAndConditions: false,
      isTripTourAdded: false,
    },
    shouldFocusError: false,
  });

  // this prevents from accessing booking flow without starting it at first step first
  if (purchaseState === null && !isIndexPage) {
    return <Navigate to="/trips/$tripId" params={{ tripId }} />;
  }

  // this prevents from going back to booking flow after completing it
  if (purchaseState === "completed" && !isSuccessPage) {
    return <Navigate to="/" />;
  }

  return (
    <main className="no-footer with-drawer main px-main pt-main">
      <Suspense fallback={<OverviewCardSkeleton />}>
        <FormProvider {...methods}>
          <Outlet />
          <ValidationTrigger />
        </FormProvider>
      </Suspense>
    </main>
  );
}
